import React, { useCallback } from 'react';
import { DeleteOutlined } from '@ant-design/icons';

import './PosQuantityStepper.scss';

export interface PosQuantityStepperProps {
  quantity: number;
  itemName: string;
  step?: number;
  onChange: (quantity: number) => void;
  onRemove: () => void;
  className?: string;
}

export const PosQuantityStepper: React.FC<PosQuantityStepperProps> = ({
  quantity,
  itemName,
  step = 1,
  onChange,
  onRemove,
  className,
}) => {
  const atMinimum = quantity <= step;
  const handleDecrement = useCallback(() => {
    if (atMinimum) {
      onRemove();
      return;
    }
    onChange(Math.max(0, quantity - step));
  }, [atMinimum, onChange, onRemove, quantity, step]);

  const handleIncrement = useCallback(() => {
    onChange(Math.max(0, quantity + step));
  }, [onChange, quantity, step]);

  const handleInput = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const next = Number(event.target.value);
      if (Number.isFinite(next)) onChange(Math.max(0, next));
    },
    [onChange],
  );

  return (
    <div className={`pos-quantity-stepper${className ? ` ${className}` : ''}`}>
      <button
        type="button"
        className={`pos-quantity-stepper__button${atMinimum ? ' pos-quantity-stepper__button--remove' : ''}`}
        onClick={handleDecrement}
        aria-label={atMinimum ? `Quitar ${itemName} del carrito` : 'Disminuir cantidad'}
        title={atMinimum ? 'Quitar del carrito' : undefined}
      >
        {atMinimum ? <DeleteOutlined aria-hidden /> : '−'}
      </button>
      <input
        type="number"
        inputMode="decimal"
        min={0}
        step={step}
        value={quantity}
        onChange={handleInput}
        className="pos-quantity-stepper__input"
        aria-label={`Cantidad para ${itemName}`}
      />
      <button
        type="button"
        className="pos-quantity-stepper__button"
        onClick={handleIncrement}
        aria-label={`Aumentar cantidad de ${itemName}`}
      >
        +
      </button>
    </div>
  );
};

export default PosQuantityStepper;
